import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { healthCheck } from "@/lib/api";
import ShareLinkBox from "@/components/share/ShareLinkBox";
import EmailShareForm from "@/components/share/EmailShareForm";
import BurnZone from "@/components/share/BurnZone";

function logDev(prefix, err) {
  if (process.env.NODE_ENV !== "production") {
    console.error(prefix, err);
  }
}

export default function ShareView() {
  const { id } = useParams();
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  const keyFragment = typeof window !== "undefined" ? window.location.hash.slice(1) : "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const meta = location.state?.meta;

  const shareLink = useMemo(
    () => `${origin}/s/${id}#${keyFragment}`,
    [origin, id, keyFragment]
  );

  const [copied, setCopied] = useState(false);
  const [burned, setBurned] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);

  useEffect(() => {
    healthCheck()
      .then((h) => setEmailEnabled(!!h.email_enabled))
      .catch((err) => logDev("[health]", err));
  }, []);

  useEffect(() => {
    if (!keyFragment) navigate("/");
  }, [keyFragment, navigate]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success(t("copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logDev("[copy]", err);
      toast.error("Copy failed");
    }
  }, [shareLink, t]);

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-16" data-testid="share-view">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-black dark:hover:text-white mb-8"
        data-testid="back-home-link"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("newSecret")}
      </Link>

      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#00ff66] mb-4">
        <span className="w-1.5 h-1.5 bg-[#00ff66] rounded-full animate-pulse" />
        encrypted // link ready
      </div>
      <h2 className="text-3xl md:text-5xl font-black tracking-tighter text-neutral-900 dark:text-white mb-3">
        {t("yourLink")}
      </h2>
      <p className="text-sm text-neutral-500 max-w-xl mb-10 leading-relaxed">
        {t("linkWarning")}
      </p>

      {burned ? (
        <div className="p-8 border border-[#ff3b30]/40 bg-[#ff3b30]/5" data-testid="burned-state">
          <p className="text-[#ff3b30] font-mono uppercase tracking-widest text-sm">
            {t("burned")}
          </p>
        </div>
      ) : (
        <>
          <ShareLinkBox
            id={id}
            shareLink={shareLink}
            copied={copied}
            onCopy={handleCopy}
            meta={meta}
          />
          <div
            className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-0 border border-black/15 dark:border-white/15"
            data-testid="actions-grid"
          >
            <EmailShareForm id={id} shareLink={shareLink} emailEnabled={emailEnabled} />
            <BurnZone id={id} onBurned={() => setBurned(true)} />
          </div>
        </>
      )}
    </div>
  );
}
